import { injectable } from 'inversify';
import { plainToClass } from 'class-transformer';

import { HttpService } from '@vk-hr-tek/core/http';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { FiltersResponse, Filter, FilterService } from '@vk-hr-tek/core/filter';

import {
  BatchEventAcceptResponse,
  BatchEventCancelResponse,
  BatchEventOperationResponse,
  CancelReasonListResponse as CancelReasonsResponse,
  EventListCountsResponse,
  EventListResponse,
  GetEventFileHashesResponse,
} from '@app/gen/events';

import {
  CancelBatchEventsDto,
  GetReviewDto,
  GetReviewFiltersDto,
  SendBatchEventsDto,
  GetHashesDto,
  UkepBatchSignDto,
} from '../dto';
import { ReviewMapper } from '../mappers/review.mapper';

@injectable()
export class EventsListService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private filterService: FilterService,
    private reviewMapper: ReviewMapper,
  ) {}

  async get(getEventsDto: GetReviewDto, availableFilters: Filter[]) {
    const eventsDto = plainToClass(
      this.filterService.buildDto(GetReviewDto, availableFilters),
      getEventsDto,
    );

    const preparedFilters = await this.reviewMapper.processFilters(eventsDto);

    const {
      limit = 50,
      offset = 0,
      sortBy = 'created_at',
      sortOrder = 'desc',
    } = eventsDto;
    const result = await this.unmarshaller.unmarshall(
      await this.http.post(
        '/event/list',
        {
          pagination: {
            limit,
            offset,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
          filters: preparedFilters,
        },
        { withSide: true, reloadIfOldVersion: true },
      ),
      EventListResponse,
    );

    return {
      data: result.events,
    };
  }

  async getFilters(getFiltersDto?: GetReviewFiltersDto) {
    if (getFiltersDto) {
      await this.validator.validateOrReject(getFiltersDto, GetReviewFiltersDto);
    }

    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/event/available_filters',
        {
          ...(!!getFiltersDto?.page && { page: getFiltersDto.page }),
        },
        { withSide: true, reloadIfOldVersion: true },
      ),
      FiltersResponse,
    );

    return result.filters;
  }

  async sendBatchAccept(sendBatchAcceptDto: SendBatchEventsDto) {
    await this.validator.validateOrReject(
      sendBatchAcceptDto,
      SendBatchEventsDto,
    );

    const result = await this.unmarshaller.unmarshall(
      this.http.post(
        '/event/batch/accept',
        {
          events: sendBatchAcceptDto.events,
        },
        {
          withSide: true,
          reloadIfOldVersion: true,
        },
      ),
      BatchEventAcceptResponse,
    );

    return {
      successes: result.successes || [],
      errors: result.errors || [],
    };
  }

  async sendBatchCancel(sendBatchCancelDto: CancelBatchEventsDto) {
    await this.validator.validateOrReject(
      sendBatchCancelDto,
      CancelBatchEventsDto,
    );

    const { events, reasonId, comment } = sendBatchCancelDto;

    const result = await this.unmarshaller.unmarshall(
      this.http.post(
        '/event/batch/cancel',
        {
          events,
          reason_id: reasonId,
          comment,
        },
        {
          withSide: true,
          reloadIfOldVersion: true,
        },
      ),
      BatchEventCancelResponse,
    );

    return {
      successes: result.successes || [],
      errors: result.errors || [],
    };
  }

  async getCancelReasons({ isCandidate }: { isCandidate?: boolean }) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.get(
        '/event/cancel_reasons',
        isCandidate ? { is_candidate: isCandidate } : {},
        { withSide: true },
      ),
      CancelReasonsResponse,
    );

    return result.reasons;
  }

  async getHashes(getHashesDto: GetHashesDto) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.post(`/event/batch/document/hash`, getHashesDto, {
        withSide: true,
      }),
      GetEventFileHashesResponse,
    );
    return result as GetEventFileHashesResponse;
  }

  async ukepBatchSign(ukepBatchSignDto: UkepBatchSignDto) {
    const result = await this.unmarshaller.unmarshall(
      await this.http.post('/event/batch/ukep_sign', ukepBatchSignDto, {
        withSide: true,
      }),
      BatchEventOperationResponse,
    );
    return result;
  }

  async getEventCounts(getEventsDto: GetReviewDto, availableFilters: Filter[]) {
    const eventsDto = plainToClass(
      this.filterService.buildDto(GetReviewDto, availableFilters),
      getEventsDto,
    );

    const preparedFilters = await this.reviewMapper.processFilters(eventsDto);

    const result = await this.unmarshaller.unmarshall(
      await this.http.post(
        '/event/list/counts',
        {
          ...(preparedFilters ? { filters: preparedFilters } : {}),
        },
        { withSide: true, reloadIfOldVersion: true },
      ),
      EventListCountsResponse,
    );

    return result;
  }
}
